/**
 * Enhanced Card Component
 * Gradient borders, glass morphism effects
 */

import React from 'react';
import { Card as MuiCard, CardProps as MuiCardProps, styled } from '@mui/material';
import { colors } from '../../theme/colors';
import { shadows } from '../../theme/shadows';

type CardVariant = 'default' | 'gradient' | 'glass' | 'elevated';

interface CustomCardProps extends Omit<MuiCardProps, 'variant'> {
  variant?: CardVariant;
  hover?: boolean;
}

const StyledCard = styled(MuiCard)<{ variant?: CardVariant; hover?: boolean }>(({ theme, variant, hover }) => {
  const baseStyles = {
    borderRadius: 16,
    backgroundImage: 'none',
  };

  const hoverStyles = hover
    ? {
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: shadows.cardHover,
        },
      }
    : {};

  switch (variant) {
    case 'gradient':
      return {
        ...baseStyles,
        ...hoverStyles,
        position: 'relative',
        background: colors.background.paper,
        boxShadow: shadows.card,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 16,
          padding: '2px',
          background: colors.primary.gradient,
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        },
      };

    case 'glass':
      return {
        ...baseStyles,
        ...hoverStyles,
        background: colors.glass.background,
        backdropFilter: `blur(${colors.glass.blur})`,
        WebkitBackdropFilter: `blur(${colors.glass.blur})`,
        border: `1px solid ${colors.glass.border}`,
        boxShadow: shadows.glass,
      };

    case 'elevated':
      return {
        ...baseStyles,
        ...hoverStyles,
        background: colors.background.paper,
        boxShadow: shadows.xl,
      };

    default:
      return {
        ...baseStyles,
        ...hoverStyles,
        background: colors.background.paper,
        boxShadow: shadows.card,
      };
  }
});

export const Card: React.FC<CustomCardProps> = ({ variant = 'default', hover = false, children, ...props }) => {
  return (
    <StyledCard variant={variant} hover={hover} {...props}>
      {children}
    </StyledCard>
  );
};
